import { isJuristicTaxId, isKnownIndividual, type PartyType } from '../party'

/**
 * Who paid, as a receipt prints them: a company with its registered name, tax ID, branch (00000 = head office) and
 * address; a person by name only, never an ID card number. The counter receipt (tms.receipt `payer*`) and the receipt
 * in the customer's name (accounting.customerReceipt) are made out the same way, from `payerDefaults`.
 */

/** A party of a bill, as a receipt's payer is found: its contact, else what the bill wrote */
export interface PayingParty {
  name?: string | null
  address?: string | null
  subdistrict?: string | null
  district?: string | null
  province?: string | null
  postcode?: string | null
  /** The contact in the book, when the bill was made from one */
  contact?: {
    /** The name receipts are made out to, when bills write it otherwise */
    registeredName?: string | null
    taxId?: string | null
    branch?: string | null
    businessType?: string | null
    address?: string | null
    subdistrict?: string | null
    district?: string | null
    province?: string | null
    postcode?: string | null
  } | null
}

export const payerAreaKeys = ['address', 'subdistrict', 'district', 'province', 'postcode'] as const

/** What is already known of the payer: on a counter receipt, or the tax ID of a 50 ทวิ they gave */
export interface KnownPayer {
  /** The name a receipt was already made out to (a counter receipt in a company's name) */
  name?: string | null
  taxId?: string | null
  branch?: string | null
  address?: string | null
  subdistrict?: string | null
  district?: string | null
  province?: string | null
  postcode?: string | null
}

export interface PayerDefaults {
  partyType: PartyType | null
  name: string | null
  taxId: string | null
  branch: string | null
  address: string | null
  subdistrict: string | null
  district: string | null
  province: string | null
  postcode: string | null
}

/**
 * Who a receipt is made out to: what is already known of them first (a counter receipt's details, a 50 ทวิ's tax ID —
 * which names whoever paid), then the book when only one party paid — the name receipts go out in (`registeredName`:
 * a company's is seldom the one bills write), a company's tax ID and branch, the address as a whole — and last the
 * payer's name and address as the bill wrote them. A person never gets a tax ID; a party known to be neither is left
 * for whoever issues it to choose
 */
export function payerDefaults(
  payerName: string | null | undefined,
  parties: readonly PayingParty[],
  known: KnownPayer = {},
): PayerDefaults {
  const [only] = parties.length === 1 ? parties : []
  const contact = only?.contact ?? null
  const taxId = [known.taxId, contact?.taxId].find((id) => isJuristicTaxId(id)) ?? null
  const person = !taxId && !!contact && isKnownIndividual({ taxId: contact.taxId, businessType: contact.businessType })
  const partyType: PartyType | null = taxId ? 'juristic' : person ? 'individual' : null
  // An address as a whole: the known one, else the contact's, else the bill's
  const from = known.address ? known : contact?.address ? contact : only
  const address = Object.fromEntries(payerAreaKeys.map((key) => [key, from?.[key] || null])) as Record<
    (typeof payerAreaKeys)[number],
    string | null
  >
  return {
    partyType,
    name: known.name?.trim() || contact?.registeredName?.trim() || payerName?.trim() || only?.name?.trim() || null,
    taxId: partyType === 'juristic' ? taxId : null,
    branch: partyType === 'juristic' ? known.branch || contact?.branch || null : null,
    ...address,
  }
}

/** A charter job's customer (accounting.customer) as the party paying it: the book's registered details, a person by its type */
export function customerParty(customer: Readonly<Record<string, unknown>>): PayingParty {
  const text = (key: string) => (customer[key] == null || customer[key] === '' ? null : String(customer[key]))
  const area = Object.fromEntries(payerAreaKeys.map((key) => [key, text(key)]))
  return {
    name: text('name'),
    ...area,
    contact: {
      registeredName: text('name'),
      taxId: text('taxId'),
      branch: text('branch'),
      businessType: customer.partyType === 'individual' ? 'individual' : null,
      ...area,
    },
  }
}
